import { createTheme } from './createTheme';
import { DARK_MODE, GRAY_SCALE } from 'constants/ThemeConstant';
import { rgba, createOpaqueTableRowColors, buildOpaqueTableRowScopedStyles, getTableRowTokens, getColoredHeaderTableTokens, buildThemeTableSorterScopedStyles, buildThemeCheckboxScopedStyles, buildThemeGroupRowCssVariables, buildThemePaginationScopedStyles, getThemePaginationTokens } from './utils';

const TET_PRIMARY = '#CF1322';
const TET_BG = '#FFF7E6';
const TET_TEXT = '#531D1D';
const TET_BORDER = '#FFD591';
const TET_GOLD = rgba('#FFD700', 0.9);
const TET_MODULE_ICON = '#FFFFFF';
const TET_TABLE_ROW_COLORS = createOpaqueTableRowColors(TET_PRIMARY, GRAY_SCALE.WHITE);

/** Sidebar custom-menu — không dùng token Menu global */
export const tetCustomMenuStyles = `
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected),
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title {
    color: ${TET_TEXT} !important;
}
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .ant-menu-item-icon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .ant-menu-item-icon {
    color: ${TET_GOLD} !important;
}
.side-nav .custom-menu .ant-menu-item-group-title,
.side-nav .custom-menu .ant-menu-item-group-title .nav-item_title {
    color: ${TET_GOLD} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected),
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open):not(.ant-menu-submenu-active) > .ant-menu-submenu-title {
    color: ${GRAY_SCALE.WHITE} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon {
    color: ${TET_GOLD} !important;
}
`;

/** Steps điều hướng form (className="step-nav") */
export const tetStepNavStyles = `
.step-nav.ant-steps .ant-steps-item-title {
    color: ${TET_TEXT} !important;
    font-size: 13px !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-title,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-title {
    color: ${TET_PRIMARY} !important;
    font-weight: 600 !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${TET_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .anticon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .anticon {
    color: ${TET_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${TET_BORDER} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .anticon {
    color: ${GRAY_SCALE.GRAY} !important;
}
`;

export const tetScopedStyles = `
${tetCustomMenuStyles}
${tetStepNavStyles}
${buildThemePaginationScopedStyles(TET_GOLD, TET_BORDER)}
.ant-table-wrapper .ant-table-thead > tr > th {
    font-weight: 600;
    text-transform: uppercase;
}
${buildThemeTableSorterScopedStyles(TET_PRIMARY)}
${buildOpaqueTableRowScopedStyles(TET_TABLE_ROW_COLORS)}
${buildThemeCheckboxScopedStyles('light')}
${buildThemeGroupRowCssVariables(TET_PRIMARY, TET_GOLD, 'light', TET_BG)}
`;

export const tetTheme = createTheme({
    primaryColor: TET_PRIMARY,
    successColor: '#FAAD14',
    warningColor: '#FA8C16',
    errorColor: '#FF4D4F',

    bgColor: TET_BG,
    textColor: TET_TEXT,
    borderColor: TET_BORDER,
    bodyBg: TET_BG,
    colorBgLayout: TET_BG,

    menuGroupTitleColor: TET_GOLD,
    moduleMenuBg: TET_PRIMARY,
    moduleMenuIconColor: TET_MODULE_ICON,
    moduleMenuItemSelectedBg: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedColor: TET_PRIMARY,
    headerNavColor: TET_BG,
    siderBg: GRAY_SCALE.WHITE,
    customMenuBg: GRAY_SCALE.WHITE,
    customMenuItemSelectedColor: TET_TEXT,
    customMenuItemSelectedBg: TET_BG,
    customMenuItemSelectedIconColor: TET_PRIMARY,
    customMenuItemSelectedBorderColor: TET_GOLD,

    footerBg: TET_PRIMARY,
    footerTextColor: GRAY_SCALE.WHITE,

    componentOverrides: {
        Layout: {
            bodyBg: TET_BG,
            headerBg: TET_BG,
            siderBg: GRAY_SCALE.WHITE,
        },
        Menu: {
            darkItemBg: 'transparent',
            darkItemColor: GRAY_SCALE.WHITE,
            darkItemSelectedBg: DARK_MODE.ACTIVE_BG_COLOR,
            darkItemSelectedColor: GRAY_SCALE.WHITE,
            darkItemHoverBg: DARK_MODE.HOVER_BG_COLOR,
            darkItemHoverColor: GRAY_SCALE.GRAY_DARK,
            darkSubMenuItemBg: DARK_MODE.BG_COLOR,
            popupBg: DARK_MODE.BG_COLOR,
            activeBarWidth: 0,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
        },
        Breadcrumb: {
            itemColor: GRAY_SCALE.GRAY,
            lastItemColor: TET_PRIMARY,
            linkColor: GRAY_SCALE.GRAY,
            linkHoverColor: TET_PRIMARY,
            separatorColor: GRAY_SCALE.GRAY,
        },
        Card: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorTextHeading: TET_PRIMARY,
            colorText: TET_TEXT,
            colorBorderSecondary: TET_BORDER,
            extraColor: TET_TEXT,
            headerBg: GRAY_SCALE.WHITE,
            actionsBg: GRAY_SCALE.WHITE,
            borderRadiusLG: 22,
        },
        Table: {
            colorText: TET_TEXT,
            colorTextHeading: GRAY_SCALE.WHITE,
            headerBg: TET_PRIMARY,
            borderColor: '#F0F0F0',
            ...getColoredHeaderTableTokens(TET_PRIMARY),
            ...getTableRowTokens(TET_TABLE_ROW_COLORS),
        },
        Steps: {
            colorPrimary: TET_PRIMARY,
            colorText: TET_TEXT,
            colorTextDescription: GRAY_SCALE.GRAY,
        },
        Input: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: TET_BORDER,
            activeBorderColor: TET_PRIMARY,
            hoverBorderColor: TET_PRIMARY,
        },
        InputNumber: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: TET_BORDER,
            activeBorderColor: TET_PRIMARY,
            hoverBorderColor: TET_PRIMARY,
        },
        DatePicker: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: TET_BORDER,
            activeBorderColor: TET_PRIMARY,
            hoverBorderColor: TET_PRIMARY,
        },

        Select: {
            colorBgContainer: GRAY_SCALE.WHITE,
            selectorBg: GRAY_SCALE.WHITE,
            colorBgElevated: GRAY_SCALE.WHITE,
            optionSelectedBg: rgba(TET_PRIMARY, 0.1),
            optionActiveBg: rgba(TET_PRIMARY, 0.06),
            colorBorder: TET_BORDER,
            activeBorderColor: TET_PRIMARY,
            hoverBorderColor: TET_PRIMARY,
        },

        Pagination: getThemePaginationTokens(TET_PRIMARY, TET_BORDER),
    },
});
